import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

type TopBannerType = {
  element: Element | null;
  originalZClass: string | null;
};

// headless component to handle some zValue of some elements when GenieAsk preview image is open
// so that the preview image is always on top of any elements
const GenieaskPreviewImageOpen = () => {
  const location = useLocation();

  const [imagePreviewOpened, setImagePreviewOpened] = useState(false);
  const [topBanner, setTopBanner] = useState<TopBannerType>({
    element: null,
    originalZClass: null,
  });

  const isGenieaskFullchat = location.pathname.includes('genieask');
  // on Fullchat page the Navbar zValue when preview image is opened should be set to '0'
  const modifiedNavbarZValue = isGenieaskFullchat ? 'z-0' : 'z-[19]';

  // observe the DOM to detect if image preview is opened or not
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (mutation.type === 'childList') {
        getTopBannerAfterNavbar();
        const attachmentPreview = document.querySelector(
          'div#attachment-preview',
        );
        attachmentPreview
          ? setImagePreviewOpened(true)
          : setImagePreviewOpened(false);
      }
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  const getTopBannerAfterNavbar = () => {
    const navbar = document.getElementById('navbar');
    // If navbar doesn't exist or has no next sibling, return
    if (!navbar || !navbar.nextElementSibling) return;

    const topBannerCandidate = navbar.nextElementSibling;
    const candidateStyle = window.getComputedStyle(topBannerCandidate);
    const height = parseInt(candidateStyle.height);
    const zIndex = parseInt(candidateStyle.zIndex);
    const isTopBanner =
      height === 64 && zIndex > 19 && candidateStyle.position === 'sticky';

    if (!isTopBanner) return;

    // only update `zClass` on the first changes where zClass is null
    const zClass = topBanner.originalZClass ?? getZClass(topBannerCandidate);
    setTopBanner(prevState => ({
      ...prevState,
      element: topBannerCandidate,
      originalZClass: zClass,
    }));
  };

  // everytime image preview is opened, it will change the z-index style of some elements so that the preview image is always on top of every elements
  useEffect(() => {
    const setZIndex = (selector: string, value: string) => {
      const element = document.querySelector(selector) as HTMLElement;
      if (element) {
        element.style.zIndex = value;
      }
    };

    const replaceZIndexClass = (
      selector: string,
      replaceValue: string,
      withValue: string,
    ) => {
      const element = document.querySelector(selector) as HTMLElement;
      if (element) {
        element.classList.remove(replaceValue);
        element.classList.add(withValue);
      }
    };

    if (imagePreviewOpened) {
      setZIndex('[data-radix-popper-content-wrapper]', '19');
      replaceZIndexClass('.arena-banner-background', 'z-20', 'z-[18]');
      replaceZIndexClass('.streak-banner', 'z-20', 'z-[18]');
      replaceZIndexClass('#navbar', 'z-100', modifiedNavbarZValue);
    } else {
      setZIndex('[data-radix-popper-content-wrapper]', '101'); // z-index: 101, to let the chatbox on top of the navbar and topbar
      replaceZIndexClass('.arena-banner-background', 'z-[18]', 'z-20');
      replaceZIndexClass('.streak-banner', 'z-[18]', 'z-20');
      replaceZIndexClass('#navbar', modifiedNavbarZValue, 'z-100');
    }
    handleTopBanner({ imagePreviewOpened });
  }, [imagePreviewOpened]);

  // specifically target the vertical scrollbar on the Completed/Expired tab that overlaps the Minichat when selecting an image as an attachment
  useEffect(() => {
    const elements = document.querySelectorAll('.ps__rail-y');
    const element = elements[elements.length - 1] as HTMLElement | undefined;
    if (element) {
      element.style.zIndex = imagePreviewOpened ? '18' : 'auto';
    }
  }, [imagePreviewOpened]);

  const handleTopBanner = ({
    imagePreviewOpened,
  }: {
    imagePreviewOpened: boolean;
  }) => {
    if (!topBanner.element) return;

    const currentZClass = getZClass();
    const modifiedZClass = imagePreviewOpened
      ? modifiedNavbarZValue
      : topBanner.originalZClass;

    topBanner.element.classList.replace(
      `${currentZClass}`,
      `${modifiedZClass}`,
    );
  };

  const getZClass = (refElement?: Element) => {
    const element = topBanner.element ?? refElement;
    const zValueBanner = 'z-100'; // static value of Top Banner. usually 'z-100'

    if (!element) {
      return zValueBanner;
    }

    const classList = element.classList;
    const zClassList = Array.from(classList).filter(className =>
      className.startsWith('z-'),
    );
    return zClassList.length > 0 ? zClassList[0] : zValueBanner;
  };

  return null;
};

export default GenieaskPreviewImageOpen;
